// Everything shown in the "Property rules" section comes from
// GET /properties/{id}/rules. Nothing is invented: a field the host left unset
// (value === null) is simply not rendered.

const isSet = (f) => f && typeof f === "object" && (f.value === true || f.value === false);

// yes/no rules written the way a guest reads them
const HOUSE_RULES = [
  ["children_allowed", "Children allowed", "Children not allowed"],
  ["pets_allowed", "Pets allowed", "No pets"],
  ["smoking_allowed", "Smoking allowed", "No smoking"],
  ["couples_allowed", "Couples allowed", "Couples not allowed"],
  ["unmarried_couples_allowed", "Unmarried couples allowed", "Unmarried couples not allowed"],
  ["local_id_accepted", "Local ID accepted", "Local ID not accepted"],
  ["early_check_in", "Early check-in possible", "No early check-in"],
  ["late_check_out", "Late check-out possible", "No late check-out"],
];

const GOOD_TO_KNOW = [
  ["wifi", "Wi-Fi available", "No Wi-Fi"],
  ["meals_provided", "Meals provided", "Meals not provided"],
  ["id_required_at_checkin", "Photo ID required at check-in", "No ID needed at check-in"],
  ["security_deposit_required", "Security deposit required", "No security deposit"],
  ["extra_guest_charge_applies", "Extra guest charges apply", "No extra guest charge"],
];

const PARKING = { free: "Free parking", paid: "Paid parking", street: "Street parking", none: "No parking" };

const pick = (rules, list) =>
  list
    .filter(([k]) => isSet(rules?.[k]))
    .map(([k, yes, no]) => ({ key: k, text: rules[k].value ? yes : no, note: (rules[k].note || "").trim() }));

export const houseRules = (rules) => pick(rules, HOUSE_RULES);

export const goodToKnow = (rules) => {
  const out = [];
  const p = (rules?.parking || "").trim();
  if (p) out.push({ key: "parking", text: PARKING[p.toLowerCase()] || `Parking: ${p}`, note: (rules.parking_note || "").trim() });
  out.push(...pick(rules, GOOD_TO_KNOW));
  const { quiet_hours_from: qf, quiet_hours_until: qu } = rules || {};
  if (qf && qu) out.push({ key: "quiet", text: `Quiet hours ${fmtTime(qf)} – ${fmtTime(qu)}`, note: "" });
  return out;
};

// "12:00" -> "12:00 PM"
export const fmtTime = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || "");
  if (!m) return hhmm || "";
  const h = Number(m[1]);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
};

// An exact instant (e.g. cancellation cutoff) always read in India time.
export const fmtIst = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (isNaN(d)) return "";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  }).format(d).replace(/\s*(am|pm)/i, (s) => s.toUpperCase());
};

// Plain-words cancellation terms. `terms` is either rules.cancellation_terms
// (no exact instant) or the fees/quote terms (carries cutoff_at for the dates chosen).
export const cancellationLines = (terms) => {
  if (!terms) return [];
  const before = terms.refund_percent_before;
  const after = terms.refund_percent_after;
  const when = terms.cutoff_at ? fmtIst(terms.cutoff_at) : null;
  const lines = [];
  if (when && before != null) {
    lines.push(
      Number(before) >= 100
        ? `Free cancellation until ${when}`
        : `Cancel before ${when} for a ${Number(before)}% refund`
    );
    lines.push(after != null && Number(after) > 0 ? `After that: ${Number(after)}% refund` : "After that: no refund");
  } else if (terms.text) {
    lines.push(terms.text.trim());
  } else if (before != null) {
    const cut = terms.cutoff_hours ? `${terms.cutoff_hours} hours before check-in` : "the cut-off";
    lines.push(`Cancel before ${cut} for a ${Number(before)}% refund`);
    lines.push(after != null && Number(after) > 0 ? `After that: ${Number(after)}% refund` : "After that: no refund");
  }
  if (terms.host_cancels) lines.push(terms.host_cancels.trim());
  return lines.filter(Boolean);
};

// ---- room media (photos + videos), cover first, no stand-ins ----
export const roomMedia = (room) => {
  const out = [];
  const seen = new Set();
  const push = (url, media_type) => {
    const u = (url || "").trim();
    if (!u || seen.has(u)) return;
    seen.add(u);
    out.push({ url: u, type: media_type === "video" ? "video" : "image" });
  };
  push(room?.cover_image, "image");
  (room?.photos || []).forEach((p) => push(p?.url, p?.media_type));
  return out;
};

// ---- guest limits ----
// The app server's fees/quote echoes the amount we send and does not price extra
// adults/children, so the cap stays the smaller of capacity and max_people.
// Capacity comes straight from the room: adults and children have their own limits.
export const guestLimits = (room) => {
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
  const maxAdults = Math.max(1, n(room?.max_adults) ?? 1);
  const maxChildren = Math.max(0, n(room?.max_children) ?? 0);
  const guestsInPrice = n(room?.guests_in_price);
  const childRuleText = room?.child_age_rule?.text || "";
  return { maxAdults, maxChildren, guestsInPrice, childRuleText, maxPeople: maxAdults + maxChildren };
};
